import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export interface PriceData {
  price: number;
  currency: string;
  updatedAt: number;
  stale?: boolean; // true when served from cache / offline
}

// Currencies Kraken supports directly for BCH
const KRAKEN_PAIRS: Record<string, string> = {
  USD: 'BCHUSD',
  EUR: 'BCHEUR',
  GBP: 'BCHGBP',
  AUD: 'BCHAUD',
  JPY: 'BCHJPY',
};

const PRICE_CACHE_PREFIX = 'bch-pos:price:';

/** Persist last known price to localStorage so it survives tab refreshes offline */
function persistPrice(data: PriceData) {
  try {
    localStorage.setItem(PRICE_CACHE_PREFIX + data.currency, JSON.stringify(data));
  } catch { /* ignore */ }
}

/** Load last known price from localStorage */
function loadPersistedPrice(currency: string): PriceData | null {
  try {
    const raw = localStorage.getItem(PRICE_CACHE_PREFIX + currency.toUpperCase());
    if (!raw) return null;
    return JSON.parse(raw) as PriceData;
  } catch { return null; }
}

/** Fetch BCH price in USD from Kraken (no API key, no CORS proxy needed) */
async function fetchBCHinUSD(): Promise<number> {
  const res = await fetch('https://api.kraken.com/0/public/Ticker?pair=BCHUSD');
  if (!res.ok) throw new Error(`Kraken fetch failed: ${res.status}`);
  const data = await res.json();
  if (data.error?.length) throw new Error(`Kraken error: ${data.error[0]}`);
  const price = parseFloat(data.result?.BCHUSD?.c?.[0]);
  if (isNaN(price)) throw new Error('Invalid Kraken price data');
  return price;
}

async function fetchBCHPrice(currency: string): Promise<PriceData> {
  const upper = currency.toUpperCase();

  // If offline, immediately return cached price marked stale
  if (!navigator.onLine) {
    const cached = loadPersistedPrice(upper);
    if (cached) return { ...cached, stale: true };
    throw new Error('Offline — no cached price available');
  }

  // Direct Kraken pair available
  if (KRAKEN_PAIRS[upper]) {
    const pair = KRAKEN_PAIRS[upper];
    const res = await fetch(`https://api.kraken.com/0/public/Ticker?pair=${pair}`);
    if (!res.ok) throw new Error(`Kraken fetch failed: ${res.status}`);
    const data = await res.json();
    if (data.error?.length) throw new Error(`Kraken error: ${data.error[0]}`);
    const result = data.result?.[pair];
    const price = parseFloat(result?.c?.[0]);
    if (isNaN(price)) throw new Error('Invalid Kraken price data');
    const priceData: PriceData = { price, currency: upper, updatedAt: Date.now(), stale: false };
    persistPrice(priceData);
    return priceData;
  }

  // Fallback: BCH/USD from Kraken × USD/currency forex rate
  const [bchUsd, fxRes] = await Promise.all([
    fetchBCHinUSD(),
    fetch(`https://open.er-api.com/v6/latest/USD`),
  ]);

  if (!fxRes.ok) throw new Error(`FX fetch failed: ${fxRes.status}`);
  const fxData = await fxRes.json();
  const rate: number = fxData?.rates?.[upper];
  if (typeof rate !== 'number') throw new Error(`No FX rate for ${currency}`);

  const priceData: PriceData = { price: bchUsd * rate, currency: upper, updatedAt: Date.now(), stale: false };
  persistPrice(priceData);
  return priceData;
}

export function useBCHPrice(currency: string) {
  const queryClient = useQueryClient();

  // When coming back online, invalidate so a fresh price is fetched
  useEffect(() => {
    const handleOnline = () => {
      queryClient.invalidateQueries({ queryKey: ['bch-price', currency] });
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [currency, queryClient]);

  return useQuery({
    queryKey: ['bch-price', currency],
    queryFn: () => fetchBCHPrice(currency),
    staleTime: 60_000,
    refetchInterval: navigator.onLine ? 90_000 : false,
    retry: navigator.onLine ? 3 : 0,
    // Seed with last known price from localStorage so UI is never blank
    initialData: () => {
      const cached = loadPersistedPrice(currency);
      return cached ? { ...cached, stale: true } : undefined;
    },
    initialDataUpdatedAt: () => {
      const cached = loadPersistedPrice(currency);
      return cached?.updatedAt ?? 0;
    },
    networkMode: 'offlineFirst',
  });
}
